// @ts-check
import React, { useEffect, useState, useRef } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faArrowLeft, faCity, faCircle, faSpinner, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

/** @typedef {{ id: number, name: string, children?: Company[] }} Company */
/** @typedef {{ id: number | string, name: string, status: string }} MenuDevice */
/** @typedef {{ devices: MenuDevice[] }} Gateway */
/**
 * `GET /menu/companies` payload: child companies per company id, and
 * gateways with their devices per company id and city.
 * @typedef {{
 *   tree: Record<number, Company[]>,
 *   devicesPerCompany: Record<string, Record<string, Gateway[]>>,
 * }} MenuResponse
 */

/**
 * Collapsible company tree with each company's cities and devices. Loads
 * the menu on first open.
 * @param {{ isCollapsed: boolean }} props `isCollapsed` true means the nav is open.
 * @returns {JSX.Element | null}
 */
const SideNav = ({ isCollapsed }) => {
  /** @type {import('react').MutableRefObject<MenuResponse | null>} */
  const apiResponseRef = useRef(null);
  /** @type {import('react').MutableRefObject<HTMLDivElement | null>} */
  const containerRef = useRef(null);
  const rootCompanyId = 0;

  const [loaded, setLoaded] = useState(false);
  const [displayedTree, setDisplayedTree] = useState(/** @type {Company[]} */ ([]));
  const [devicesPerCompany, setDevicesPerCompany] = useState(/** @type {MenuResponse['devicesPerCompany']} */ ({}));
  const [citiesPerCompany, setCitiesPerCompany] = useState(/** @type {Record<string, string[]>} */ ({}));
  const [currentLocation, setCurrentLocation] = useState(/** @type {Company | null} */ (null));
  const [previousLocation, setPreviousLocation] = useState(/** @type {Company | null} */ (null));
  const [backtrack, setBacktrack] = useState(/** @type {Company[]} */ ([]));
  const [mainLocationIndex, setMainLocationIndex] = useState(/** @type {number | null} */ (null));

  useEffect(() => {
    if (!isCollapsed) {
      return;
    }

    const fetchCompanies = async () => {
      const res = await fetch(`${process.env.REACT_APP_CLOUD_CONNECT_API}/menu/companies`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('JWT_TOKEN')}`,
        }
      });

      /** @type {MenuResponse} */
      const response = await res.json();
      apiResponseRef.current = response;

      const newDevicesPerCompany = { ...response.devicesPerCompany };
      /** @type {Record<string, string[]>} */
      const newCitiesPerCompany = {};

      for (const companyId of Object.keys(newDevicesPerCompany)) {
        newCitiesPerCompany[companyId] = Object.keys(newDevicesPerCompany[companyId]);

        for (const city of Object.keys(newDevicesPerCompany[companyId])) {
          for (let idx = 0; idx < newDevicesPerCompany[companyId][city].length; idx++) {
            newDevicesPerCompany[companyId][city][idx].devices.sort((a, b) =>
              a.name > b.name ? 1 : -1
            );
          }
        }
      }

      setDevicesPerCompany(newDevicesPerCompany);
      setCitiesPerCompany(newCitiesPerCompany);
      setDisplayedTree(response.tree[rootCompanyId] ?? []);
      setLoaded(true);
    };

    fetchCompanies();
  }, [isCollapsed]);

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  /** @param {number} startFrom Company id whose children to show. */
  const buildTree = (startFrom) => {
    setDisplayedTree(apiResponseRef.current?.tree[startFrom] ?? []);
  };

  /**
   * @param {Company} previousCompany
   * @param {Company} company
   */
  const forwardTree = (previousCompany, company) => {
    setBacktrack(prev => [...prev, previousCompany]);
    setPreviousLocation(previousCompany);
    setCurrentLocation(company);
    setMainLocationIndex(null);
    buildTree(company.id);
    scrollToTop();
  };

  const backTree = () => {
    const newBacktrack = [...backtrack];
    const popped = newBacktrack.pop();
  
    if (newBacktrack.length === 0) {
      setCurrentLocation(null);
      setPreviousLocation(null);
      setMainLocationIndex(null);
      buildTree(rootCompanyId);
    } else {
      setCurrentLocation(popped ?? null);
      setPreviousLocation(newBacktrack[newBacktrack.length - 1]);
      buildTree(popped ? popped.id : rootCompanyId);
    }
  
    setBacktrack(newBacktrack);
    scrollToTop();
  };

  /** @param {number} companyId */
  const toggleCompany = (companyId) => {
    setMainLocationIndex(prev => prev === companyId ? null : companyId);
  };

  /** @param {number | string} deviceId */
  const navigateToDevice = (deviceId) => {
    window.location.href = `${process.env.REACT_APP_CLOUD_CONNECT_APP}/device/data/${deviceId}`;
  };

  /**
   * @param {number} companyId
   * @returns {JSX.Element[] | undefined}
   */
  const renderCitiesAndDevices = (companyId) => {
    return citiesPerCompany[companyId]?.map(city => (
      <div key={city}>
        <div className='flex py-2 px-3 justify-between text-gray-400 bg-gray-700'>
          <span>{city}</span>
          <FontAwesomeIcon icon={faCity} />
        </div>
        {devicesPerCompany[companyId][city]?.map((gateway) =>
          gateway.devices.map((device) => (
            <div
              key={device.id}
              onClick={() => navigateToDevice(device.id)}
              className='flex items-center justify-between py-2 px-3 pl-6 cursor-pointer hover:bg-cyan-500 hover:text-white bg-gray-800 text-sm'
            >
              <div className='flex items-center'>
                <FontAwesomeIcon
                  icon={faCircle}
                  className={`mr-2 text-xs ${device.status === 'online' ? 'text-green-500' : 'text-red-500'}`}
                />
                {device.name}
              </div>
              <FontAwesomeIcon icon={faExternalLinkAlt} className='text-xs text-gray-500' />
            </div>
          ))
        )}
      </div>
    ));
  };

  if (!isCollapsed) {
    return null;
  }

  if (!loaded) {
    return (
      <div className='z-50 fixed left-0 h-screen text-gray-400 bg-gray-800 w-64 flex items-center justify-center'>
        <FontAwesomeIcon icon={faSpinner} spin className='text-3xl' />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${isCollapsed
        ? 'translate-x-64 transition duration-500 ease-in-out'
        : 'translate-x-0 transition duration-500 ease-in-out'}
        z-50 fixed -left-64 h-screen text-sm overflow-y-auto text-gray-400 bg-gray-800 w-64`}
    >
      {currentLocation && previousLocation && (
        <>
          <div
            onClick={backTree}
            className='flex items-center py-3 px-3 bg-cyan-600 text-white cursor-pointer hover:bg-cyan-700 sticky top-0 z-10'
          >
            <FontAwesomeIcon icon={faArrowLeft} className='mr-2' />
            {previousLocation.name}
          </div>
          <div className='py-2 px-3 text-white bg-gray-700'>
            {currentLocation.name}
          </div>
        </>
      )}

      {displayedTree.map((item) => (
        <div key={item.id}>
          <div
            onClick={() => toggleCompany(item.id)}
            className={`flex py-2 px-3 justify-between cursor-pointer hover:bg-cyan-500 hover:text-white transition-all
              ${mainLocationIndex === item.id ? 'bg-cyan-700 text-white' : ''}`}
          >
            <span>{item.name}</span>
            <FontAwesomeIcon icon={faBuilding} />
          </div>

          {mainLocationIndex === item.id && item.children?.map((subLocation) => (
            <div
              key={subLocation.id}
              onClick={(e) => { e.stopPropagation(); forwardTree(item, subLocation); }}
              className='flex py-2 px-3 pl-6 justify-between cursor-pointer hover:bg-cyan-500 hover:text-white bg-gray-700'
            >
              <span>{subLocation.name}</span>
              <FontAwesomeIcon icon={faBuilding} />
            </div>
          ))}

          {mainLocationIndex === item.id && renderCitiesAndDevices(item.id)}

          {mainLocationIndex === item.id &&
            (!devicesPerCompany[item.id] || Object.keys(devicesPerCompany[item.id]).length === 0) &&
            (!item.children || item.children.length === 0) && (
              <div className='py-2 px-3 pl-6 text-gray-500'>No Systems</div>
          )}
        </div>
      ))}

      {currentLocation && renderCitiesAndDevices(currentLocation.id)}

      {currentLocation &&
        (!devicesPerCompany[currentLocation.id] || Object.keys(devicesPerCompany[currentLocation.id]).length === 0) &&
        (!apiResponseRef.current?.tree[currentLocation.id] || apiResponseRef.current?.tree[currentLocation.id].length === 0) && (
          <div className='py-2 px-3 text-gray-500'>No Systems</div>
      )}
    </div>
  );
};

export default SideNav;